(() => {
  const executingScript = document.currentScript;
  const scriptAssetUrl = executingScript && executingScript.src ? executingScript.src : '';
  const assetBaseUrl = scriptAssetUrl ? new URL('.', scriptAssetUrl) : null;

  const resolveAsset = filename => {
    if (!filename) return '';
    if (/^(?:https?:)?\/\//i.test(filename) || filename.startsWith('data:') || filename.startsWith('/')) return filename;
    const mappedAssets = window.__foundryInteractiveHeroAssets || {};
    if (typeof mappedAssets[filename] === 'string' && mappedAssets[filename]) {
      return mappedAssets[filename];
    }
    return assetBaseUrl ? new URL(filename, assetBaseUrl).href : filename;
  };

  const rotate = (point, center, degrees) => {
    const radians = degrees * Math.PI / 180;
    const cosine = Math.cos(radians);
    const sine = Math.sin(radians);
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    return {x:center.x + dx*cosine - dy*sine,y:center.y + dx*sine + dy*cosine};
  };

  const initialize = root => {
    if (!root || root.dataset.foundryInitialized === 'true') return;
    const dataElement = root.querySelector('.foundry-hero__data');
    const image = root.querySelector('.foundry-hero__image');
    const magnifiers = root.querySelector('.foundry-hero__magnify-layer');
    const callouts = root.querySelector('.foundry-hero__callout-layer');
    const tooltip = root.querySelector('.foundry-hero__tooltip');
    const badge = root.querySelector('.foundry-hero__badge');
    const title = root.querySelector('.foundry-hero__tooltip-title');
    const description = root.querySelector('.foundry-hero__tooltip-description');
    if (!image || !magnifiers || !callouts || !tooltip || !badge || !title || !description) return;

    let data;
    try {
      if (root.dataset.foundryConfig) {
        data = JSON.parse(root.dataset.foundryConfig);
      } else if (dataElement) {
        data = JSON.parse(dataElement.textContent || '{}');
      } else {
        const configMap = window.__foundryInteractiveHeroConfigs || {};
        data = root.id && configMap[root.id] ? configMap[root.id] : null;
        if (!data) return;
      }
    } catch {
      return;
    }

    root.dataset.foundryInitialized = 'true';

    if (image.dataset.foundryAsset) {
      image.src = resolveAsset(image.dataset.foundryAsset);
    }

    if (Array.isArray(data.hotspots)) {
      data.hotspots = data.hotspots.map(hotspot => ({
        ...hotspot,
        imageSource: hotspot.imageSource || resolveAsset(hotspot.imageAsset || ''),
      }));
    } else {
      data.hotspots = [];
    }

    const masks = new Map();
    let active = null;
    let hitLayer = root.querySelector('.foundry-hero__hit-layer');
    if (!hitLayer) {
      hitLayer = document.createElement('div');
      hitLayer.className = 'foundry-hero__hit-layer';
      hitLayer.setAttribute('aria-label', 'Interactive image hotspots');
      root.append(hitLayer);
    }

    const prepareMasks = async () => {
      await Promise.all(data.hotspots.filter(h => h.type === 'image' && h.imageSource).map(h => new Promise(resolve => {
        const maskImage = new Image();
        maskImage.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = maskImage.naturalWidth;
          canvas.height = maskImage.naturalHeight;
          const context = canvas.getContext('2d', {willReadFrequently:true});
          if (!context) { resolve(); return; }
          context.drawImage(maskImage,0,0);
          masks.set(h.id,{width:canvas.width,height:canvas.height,data:context.getImageData(0,0,canvas.width,canvas.height).data});
          resolve();
        };
        maskImage.onerror = resolve;
        maskImage.src = h.imageSource;
      })));
    };

    const localPoint = event => {
      const bounds = image.getBoundingClientRect();
      return {x:(event.clientX-bounds.left)/bounds.width*data.width,y:(event.clientY-bounds.top)/bounds.height*data.height};
    };

    const contains = (hotspot, point) => {
      if (hotspot.width <= 0 || hotspot.height <= 0) return false;
      const center = {x:hotspot.x+hotspot.width/2,y:hotspot.y+hotspot.height/2};
      const local = rotate(point,center,-hotspot.rotation);
      if (hotspot.type === 'circle') {
        const nx=(local.x-center.x)/(hotspot.width/2);
        const ny=(local.y-center.y)/(hotspot.height/2);
        return nx*nx+ny*ny<=1;
      }
      if (local.x<hotspot.x||local.x>hotspot.x+hotspot.width||local.y<hotspot.y||local.y>hotspot.y+hotspot.height) return false;
      if (hotspot.type !== 'image') return true;
      const mask=masks.get(hotspot.id);
      if (!mask) return false;
      const mx=Math.min(mask.width-1,Math.max(0,Math.floor((local.x-hotspot.x)/hotspot.width*mask.width)));
      const my=Math.min(mask.height-1,Math.max(0,Math.floor((local.y-hotspot.y)/hotspot.height*mask.height)));
      return mask.data[(my*mask.width+mx)*4+3] >= data.alphaThreshold;
    };

    const renderMagnifiers = () => {
      magnifiers.replaceChildren();
      for (const hotspot of data.hotspots) {
        if (hotspot.type !== 'magnify' || !hotspot.imageSource) continue;
        const magnifier=document.createElement('img');
        magnifier.className='foundry-hero__magnify-image';
        magnifier.src=hotspot.imageSource;
        magnifier.alt='';
        magnifier.dataset.hotspotId=hotspot.id;
        magnifier.style.left=(hotspot.x/data.width*100)+'%';
        magnifier.style.top=(hotspot.y/data.height*100)+'%';
        magnifier.style.width=(hotspot.width/data.width*100)+'%';
        magnifier.style.height=(hotspot.height/data.height*100)+'%';
        magnifier.style.transform='rotate('+hotspot.rotation+'deg) scale(1)';
        magnifiers.append(magnifier);
      }
    };

    const setMagnified = hotspotId => {
      magnifiers.querySelectorAll('.foundry-hero__magnify-image').forEach(magnifier => {
        const hotspot=data.hotspots.find(h=>h.id===magnifier.dataset.hotspotId);
        if(!hotspot) return;
        const isActive=Boolean(hotspotId&&hotspot.id===hotspotId);
        const scale=isActive?(hotspot.magnifyScale||2):1;
        const restingLeft=hotspot.x/data.width*100;
        const restingTop=hotspot.y/data.height*100;
        const widthPercent=hotspot.width/data.width*100;
        const heightPercent=hotspot.height/data.height*100;
        const centeredLeft=50-widthPercent/2;
        const centeredTop=50-heightPercent/2;
        magnifier.style.left=(isActive?centeredLeft:restingLeft)+'%';
        magnifier.style.top=(isActive?centeredTop:restingTop)+'%';
        magnifier.style.transform='rotate('+hotspot.rotation+'deg) scale('+scale+')';
        magnifier.classList.toggle('is-magnified',isActive);
      });
    };

    const renderCallouts = () => {
      callouts.replaceChildren();
      for (const hotspot of data.hotspots) {
        if (!hotspot.showNew && !hotspot.showBestSeller) continue;
        const center={x:hotspot.x+hotspot.width/2,y:hotspot.y+hotspot.height/2};
        const anchor=rotate({x:center.x,y:hotspot.y},center,hotspot.rotation);
        const callout=document.createElement('div');
        callout.className='foundry-hero__callout';
        callout.dataset.hotspotId=hotspot.id;
        callout.style.left=(anchor.x/data.width*100)+'%';
        callout.style.top=(anchor.y/data.height*100)+'%';
        if(hotspot.showNew){const label=document.createElement('span');label.className='foundry-hero__callout-label';label.textContent='New!';callout.append(label);}
        if(hotspot.showBestSeller){const label=document.createElement('span');label.className='foundry-hero__callout-label foundry-hero__callout-label--best';label.textContent='Best Seller!';callout.append(label);}
        callouts.append(callout);
      }
    };

    const setActiveCallout = hotspotId => {
      callouts.querySelectorAll('.foundry-hero__callout').forEach(callout => {
        callout.hidden = Boolean(hotspotId && callout.dataset.hotspotId === hotspotId);
      });
    };

    const find = point => {
      for(let i=data.hotspots.length-1;i>=0;i--){
        const hotspot=data.hotspots[i];
        if(hotspot.type==='magnify') continue;
        if(contains(hotspot,point)) return hotspot;
      }
      return null;
    };
    const findMagnify = point => {
      for(let i=data.hotspots.length-1;i>=0;i--){
        const hotspot=data.hotspots[i];
        if(hotspot.type==='magnify'&&contains(hotspot,point)) return hotspot;
      }
      return null;
    };

    const hide = () => {tooltip.hidden=true;active=null;setActiveCallout(null);setMagnified(null);root.classList.remove('is-linked');};
    const showHotspot = (hotspot,event) => {
      active=hotspot||null;
      setActiveCallout(hotspot ? hotspot.id : null);
      root.classList.toggle('is-linked',Boolean(hotspot&&hotspot.linkUrl));
      if(!hotspot||(!hotspot.badgeText&&!hotspot.hoverTitle&&!hotspot.hoverDescription)){tooltip.hidden=true;return;}
      badge.textContent=hotspot.badgeText||'';badge.hidden=!hotspot.badgeText;
      title.textContent=hotspot.hoverTitle||'';title.hidden=!hotspot.hoverTitle;
      description.textContent=hotspot.hoverDescription||'';description.hidden=!hotspot.hoverDescription;
      tooltip.hidden=false;
      const bounds=root.getBoundingClientRect();
      tooltip.style.left=Math.max(8,Math.min(event.clientX-bounds.left+14,bounds.width-tooltip.offsetWidth-8))+'px';
      tooltip.style.top=Math.max(8,Math.min(event.clientY-bounds.top+14,bounds.height-tooltip.offsetHeight-8))+'px';
    };

    const findAlpha = point => {
      for(let i=data.hotspots.length-1;i>=0;i--){
        const hotspot=data.hotspots[i];
        if(hotspot.type==='image'&&contains(hotspot,point)) return hotspot;
      }
      return null;
    };

    const followAdvancedHotspots = event => {
      const point=localPoint(event);
      const alphaHotspot=findAlpha(point);
      if(alphaHotspot){
        setMagnified(null);
        showHotspot(alphaHotspot,event);
        return;
      }
      // Do not clear Rectangle/Circle hover here; their direct DOM target owns it.
      const magnifyHotspot=findMagnify(point);
      setMagnified(magnifyHotspot ? magnifyHotspot.id : null);
      if(!magnifyHotspot && active && active.type === 'image') hide();
    };

    const renderHitAreas = () => {
      hitLayer.replaceChildren();
      for (const hotspot of data.hotspots) {
        // Build 0033: Alpha hotspots never receive rectangular DOM hit targets.
        // Their editor bounding box is placement geometry only; live hits come
        // exclusively from per-pixel alpha-mask testing at the hero level.
        if (hotspot.type === 'image') continue;
        const hit = document.createElement(hotspot.linkUrl ? 'a' : 'button');
        hit.className='foundry-hero__hit-area'+(hotspot.type==='circle'?' foundry-hero__hit-area--circle':'')+(hotspot.linkUrl?' is-linked':'');
        hit.dataset.hotspotId=hotspot.id;
        hit.style.left=(hotspot.x/data.width*100)+'%';
        hit.style.top=(hotspot.y/data.height*100)+'%';
        hit.style.width=(hotspot.width/data.width*100)+'%';
        hit.style.height=(hotspot.height/data.height*100)+'%';
        hit.style.transform='rotate('+hotspot.rotation+'deg)';
        if (hotspot.linkUrl) {
          hit.href=hotspot.linkUrl;
          if (hotspot.openInNewTab) { hit.target='_blank'; hit.rel='noopener noreferrer'; }
          hit.setAttribute('aria-label', hotspot.hoverTitle || hotspot.badgeText || 'Open hotspot link');
        } else {
          hit.type='button';
          hit.setAttribute('aria-label', hotspot.hoverTitle || hotspot.badgeText || 'Interactive hotspot');
        }
        const directMove = event => {
          if (hotspot.type === 'magnify') setMagnified(hotspot.id);
          showHotspot(hotspot,event);
        };
        hit.addEventListener('pointerenter',directMove);
        hit.addEventListener('pointermove',directMove);
        hit.addEventListener('pointerleave',hide);
        hit.addEventListener('focus',event => {
          const bounds=hit.getBoundingClientRect();
          showHotspot(hotspot,{clientX:bounds.left+bounds.width/2,clientY:bounds.top+bounds.height/2});
        });
        hit.addEventListener('blur',hide);
        hit.addEventListener('click',event => {
          if (!hotspot.linkUrl) event.preventDefault();
        });
        hitLayer.append(hit);
      }
    };

    root.addEventListener('pointermove',followAdvancedHotspots);
    root.addEventListener('pointerleave',hide);
    root.addEventListener('click',event => {
      const alphaHotspot=findAlpha(localPoint(event));
      if(!alphaHotspot || !alphaHotspot.linkUrl) return;
      event.preventDefault();
      event.stopPropagation();
      if(alphaHotspot.openInNewTab) window.open(alphaHotspot.linkUrl,'_blank','noopener,noreferrer');
      else window.location.href=alphaHotspot.linkUrl;
    }, true);
    root.tabIndex=-1;
    renderMagnifiers();
    renderCallouts();
    renderHitAreas();
    void prepareMasks();
  };

  const initializeAll = container => {
    (container || document).querySelectorAll('.foundry-hero').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), {once:true});
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', event => initializeAll(event.target));

  // Build 0051: public initialization hook + late-render/Horizon support.
  // Existing heroes remain protected by data-foundry-initialized, so this
  // does not double-bind the working embedded-page instances.
  window.FoundryInteractiveHero = window.FoundryInteractiveHero || {};
  window.FoundryInteractiveHero.initializeAll = initializeAll;

  const foundryObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('.foundry-hero')) initialize(node);
        initializeAll(node);
      }
    }
  });

  if (document.documentElement) {
    foundryObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();